'use client'

import { useEffect, useState } from 'react'
import { MdEventRepeat, MdInfoOutline } from 'react-icons/md'

interface RescheduleComponentModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: (input: {
        scheduledAt: string
        arrivalOrder: boolean
        reason?: string
    }) => Promise<void>
    loading?: boolean
    /** Nome do componente — o paciente é avisado citando ele. */
    componentName: string
    /** Data atual do componente, para o campo abrir preenchido. */
    currentScheduledAt?: string | null
    currentArrivalOrder?: boolean
}

/**
 * Remarcação de UM componente de uma venda de pacote.
 *
 * Num pacote raramente move tudo junto: a cirurgia adia e a consulta de retorno fica. Por
 * isso a remarcação é por linha — move só a data daquele componente e o evento de agenda
 * dele. Ver docs/catalog-package-pricing.md.
 */
export default function RescheduleComponentModal({
    isOpen,
    onClose,
    onConfirm,
    loading = false,
    componentName,
    currentScheduledAt,
    currentArrivalOrder = false,
}: RescheduleComponentModalProps) {
    const [scheduledAt, setScheduledAt] = useState('')
    const [arrivalOrder, setArrivalOrder] = useState(false)
    const [reason, setReason] = useState('')

    useEffect(() => {
        if (!isOpen) return
        setArrivalOrder(currentArrivalOrder)
        // `datetime-local` não aceita fuso: corta no minuto. Por ordem de chegada, o
        // campo é só data — a hora não existe.
        setScheduledAt(
            currentScheduledAt
                ? currentArrivalOrder
                    ? currentScheduledAt.substring(0, 10)
                    : currentScheduledAt.substring(0, 16)
                : ''
        )
        setReason('')
    }, [isOpen, currentScheduledAt, currentArrivalOrder])

    if (!isOpen) return null

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-lg">
                <div className="flex items-center gap-2 mb-1">
                    <MdEventRepeat className="text-xl text-primary" />
                    <h3 className="font-semibold text-lg">Remarcar componente</h3>
                </div>
                <p className="text-sm text-base-content/60 mb-4">{componentName}</p>

                <div className="form-control mb-3">
                    <label className="label">
                        <span className="label-text font-medium">Nova data</span>
                    </label>
                    <input
                        type={arrivalOrder ? 'date' : 'datetime-local'}
                        className="input input-bordered w-full"
                        value={scheduledAt}
                        onChange={e => setScheduledAt(e.target.value)}
                    />
                </div>

                <label className="label cursor-pointer justify-start gap-2 mb-3">
                    <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={arrivalOrder}
                        onChange={e => {
                            setArrivalOrder(e.target.checked)
                            // Trocar o modo muda o formato do campo: sem cortar, a hora
                            // ficaria pendurada numa data sem hora.
                            setScheduledAt(prev =>
                                e.target.checked
                                    ? prev.substring(0, 10)
                                    : prev.substring(0, 10)
                            )
                        }}
                    />
                    <span className="label-text">
                        Por ordem de chegada (tem dia, não tem hora)
                    </span>
                </label>

                <div className="form-control mb-4">
                    <label className="label">
                        <span className="label-text font-medium">
                            Motivo (opcional)
                        </span>
                    </label>
                    <textarea
                        className="textarea textarea-bordered w-full"
                        rows={2}
                        placeholder="Ex.: paciente pediu para adiar"
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                    />
                </div>

                <div className="alert alert-info text-sm mb-4">
                    <MdInfoOutline className="text-lg" />
                    <span>
                        Só este componente muda de data. Os demais do pacote ficam como
                        estão, e o paciente é avisado citando este item.
                    </span>
                </div>

                <div className="modal-action">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!scheduledAt || loading}
                        onClick={() =>
                            onConfirm({
                                scheduledAt: new Date(scheduledAt).toISOString(),
                                arrivalOrder,
                                reason: reason.trim() || undefined,
                            })
                        }
                    >
                        {loading ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            'Remarcar'
                        )}
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}
